/**
 * Unified main content: one EventViewer, responsive layout (desktop column vs mobile tabs).
 * Renders exactly one EventViewer in the tree; layout switches by isLgUp.
 */
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Tab } from '@headlessui/react';

import { useUserSessionContext } from 'features/session/userSessionStore';
import { LocalStorage } from 'shared/utils/storage';
import EVENT_NAMES from 'shared/constants/events';
import { getUrlParameter } from 'shared/utils/common';
import Emitter from 'shared/events/emitter';
import { normalizeSupportedLanguage } from 'shared/utils/supportedLanguage';
import i18n from 'i18n';

import EventViewer from 'features/events/components/EventViewer';
import AllSchedulePanels from 'features/markets/components/AllSchedulePanels';
import UserCreditInfo from 'features/session/components/UserCreditInfo';
import RebuyButton from 'features/tickets/components/RebuyButton';
import MyBets from 'features/tickets/components/MyBets';
import { REBUY_ENABLED } from 'features/tickets/constants/feature-flags';
import { useMobileTabsContext } from 'app/stores/mobile-tabs-store';
import { cn } from 'lib/utils';

const UserCreditInfoC = UserCreditInfo as unknown as React.ComponentType;

export interface UnifiedMainContentProps {
  isLgUp: boolean;
  tvPanelRunning: boolean;
  tvAccordionExpanded: boolean;
  setTvAccordionExpanded: React.Dispatch<React.SetStateAction<boolean>>;
  theaterMode: boolean;
  setTheaterMode: React.Dispatch<React.SetStateAction<boolean>>;
  currentEventIdWhenTvRunning: number | string | null;
  embedded: boolean;
  onOpenLogin: () => void;
}

function UnifiedMainContent({
  isLgUp,
  tvPanelRunning,
  tvAccordionExpanded,
  setTvAccordionExpanded,
  theaterMode,
  setTheaterMode,
  currentEventIdWhenTvRunning,
  embedded,
  onOpenLogin,
}: UnifiedMainContentProps) {
  const { t } = useTranslation();
  const [{ user }, dispatch] = useUserSessionContext();
  const userObj = user as Record<string, unknown> | null | undefined;
  const isLoggedIn = userObj && !(userObj.guest as boolean);
  const { tabIndex, setActiveTab } = useMobileTabsContext();

  const currentLanguage = normalizeSupportedLanguage(userObj?.language as string | undefined);
  const currentOddsFormat = (userObj?.odds_format as string | undefined) || 'decimal';

  React.useEffect(() => {
    if (userObj?.language) {
      const normalized = normalizeSupportedLanguage(userObj.language as string);
      if (i18n.language !== normalized) {
        i18n.changeLanguage(normalized);
      }
    }
  }, [userObj?.language]);

  const languages = [
    { code: 'en', name: 'English', flag: '🇺🇸' },
    { code: 'es', name: 'Español', flag: '🇪🇸' },
    { code: 'it', name: 'Italiano', flag: '🇮🇹' },
  ];

  const oddsFormatOptions = [
    { code: 'decimal', name: 'Decimal (2.50)' },
    { code: 'fractional', name: 'Fractional (3/2)' },
    { code: 'american', name: 'American (+150)' },
  ];

  const handleLanguageChange = (languageCode: string) => {
    i18n.changeLanguage(languageCode);
    if (userObj) {
      const updatedUser = { ...userObj, language: languageCode.toUpperCase() };
      dispatch({ type: 'SET_USER', payload: updatedUser });
    }
  };

  const handleOddsFormatChange = (oddsFormat: string) => {
    if (userObj) {
      const updatedUser = { ...userObj, odds_format: oddsFormat };
      dispatch({ type: 'SET_USER', payload: updatedUser });
    }
  };

  const handleLogout = () => {
    Emitter.emit(EVENT_NAMES.AUTH.LOGOUT_PROCEED, {});
  };

  const showLogout = (userObj && !(userObj.guest as boolean)) || getUrlParameter('s');

  const eventViewerCommonProps = {
    inline_key: 'event-viewer' as const,
    accordionExpanded: tvAccordionExpanded,
    onAccordionExpandedChange: setTvAccordionExpanded,
    theaterMode,
    onTheaterModeChange: setTheaterMode,
  };

  return (
    <div className={isLgUp ? 'c7-desktop-layout flex flex-col flex-1 min-h-0 gap-4' : 'c7-mobile-layout flex flex-col flex-1 min-h-0 relative z-20'}>
      {/* EventViewer: always child 0 at consistent tree depth so React preserves the instance across breakpoints */}
      <div
        className={
          isLgUp
            ? `c7-panel overflow-hidden c7-sport-glow transition-[max-height] duration-300 ease-out ${theaterMode ? 'min-h-0 flex flex-col flex-[2]' : 'flex-shrink-0'}`
            : 'w-full'
        }
        style={
          isLgUp
            ? {
                maxHeight: (tvPanelRunning || tvAccordionExpanded)
                  ? (theaterMode ? 'none' : embedded ? 'min(80%, 600px)' : '600px')
                  : '80px',
                minHeight: 0,
                overflow: 'hidden',
              }
            : { display: tabIndex === 0 ? 'block' : 'none' }
        }
      >
        <div className={isLgUp ? 'c7-panel overflow-hidden c7-sport-glow min-h-0 min-w-0 flex-1 w-full' : undefined}>
          <EventViewer
            {...eventViewerCommonProps}
            layoutContext={isLgUp ? 'desktop' : 'mobile'}
            eventIdForBetsPanel={isLgUp ? currentEventIdWhenTvRunning : undefined}
            isActiveLayout={isLgUp || tabIndex === 0}
          />
        </div>
      </div>

      {isLgUp ? (
        <div className="c7-panel overflow-hidden c7-sport-glow flex-1 flex flex-col w-full min-w-0 p-4">
          <AllSchedulePanels />
        </div>
      ) : (
        <Tab.Group
          as="div"
          className="lg:hidden flex flex-col flex-1 min-h-0 overflow-hidden"
          selectedIndex={tabIndex}
          onChange={(index) => {
            const tabs = ['bets', 'wallet', 'settings'];
            if (index === 1 && !isLoggedIn) {
              onOpenLogin();
              return;
            }
            setActiveTab((tabs[index] ?? 'bets') as 'bets' | 'wallet' | 'settings');
          }}
        >
          <Tab.Panels className="flex-1 min-h-0 overflow-auto pb-36">
            <Tab.Panel className="p-2 motion-safe:animate-fade-in">
              <div className="grid grid-cols-1 gap-2">
                <AllSchedulePanels />
              </div>
            </Tab.Panel>
            <Tab.Panel className="p-2 motion-safe:animate-fade-in">
              {!isLoggedIn ? (
                <div className="wallet-content flex flex-col items-center justify-center py-12 px-4">
                  <p className="text-muted-foreground text-center mb-6">
                    {t('LOGIN_TO_SEE_BETS', 'Log in to see your bets.')}
                  </p>
                  <button
                    type="button"
                    onClick={onOpenLogin}
                    className="w-full max-w-xs flex items-center justify-center gap-2 py-3 px-4 rounded-lg c7-sport-bg c7-sport-bg-hover c7-sport-ring text-[var(--sport-on-color)] font-medium"
                    aria-label={t('LOGIN', 'Login')}
                  >
                    <i className="bi bi-box-arrow-in-right text-lg" aria-hidden="true" />
                    <span>{t('LOGIN', 'Login')}</span>
                  </button>
                </div>
              ) : (
                <div className="space-y-4">
                  <UserCreditInfoC />
                  {REBUY_ENABLED && (
                    <div className="flex gap-2">
                      <button
                        className="flex-1 flex items-center justify-center gap-2 py-2.5 px-3 rounded-lg text-sm transition-all c7-sport-bg c7-sport-bg-hover c7-sport-ring"
                        onClick={() => {
                          const event = new CustomEvent('trigger-rebuy');
                          window.dispatchEvent(event);
                        }}
                        aria-label={t('DO_REBUY')}
                      >
                        <i className="bi bi-bank2" />
                        <span className="font-medium">{t('DO_REBUY')}</span>
                      </button>
                      <RebuyButton isMobile={true} />
                    </div>
                  )}
                  <MyBets />
                </div>
              )}
            </Tab.Panel>
            <Tab.Panel className="p-2 motion-safe:animate-fade-in">
              <div className="settings-content">
                <div className="settings-header mb-6 text-center">
                  <h4 className="text-lg font-semibold text-gray-900">{t('USER_SETTINGS')}</h4>
                </div>
                <div className="settings-body">
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <label className="flex items-center text-sm font-medium text-foreground">
                        <i className="bi bi-translate mr-2 text-[var(--sport-color)]" />
                        {t('LANGUAGE')}
                      </label>
                      <div className="flex gap-2 flex-wrap">
                        {languages.map((lang) => (
                          <button
                            key={lang.code}
                            type="button"
                            className={cn(
                              'flex items-center px-3 py-2 border rounded-md text-sm transition-all',
                              currentLanguage === lang.code
                                ? 'border-[var(--sport-color)] bg-[var(--sport-color)] text-[var(--sport-on-color)]'
                                : 'border-border bg-background text-foreground hover:border-[var(--sport-color)] hover:bg-muted'
                            )}
                            onClick={() => handleLanguageChange(lang.code)}
                            title={lang.name}
                          >
                            <span className="text-lg mr-2">{lang.flag}</span>
                            <span className="font-medium">{lang.name}</span>
                          </button>
                        ))}
                      </div>
                    </div>
                    <div className="space-y-2">
                      <label className="flex items-center text-sm font-medium text-foreground">
                        <i className="bi bi-percent mr-2 text-[var(--sport-color)]" />
                        {t('ODDS_FORMAT')}
                      </label>
                      <div className="flex gap-2 flex-wrap">
                        {oddsFormatOptions.map((option) => (
                          <button
                            key={option.code}
                            type="button"
                            className={cn(
                              'flex items-center px-3 py-2 border rounded-md text-sm transition-all',
                              currentOddsFormat === option.code
                                ? 'border-[var(--sport-color)] bg-[var(--sport-color)] text-[var(--sport-on-color)]'
                                : 'border-border bg-background text-foreground hover:border-[var(--sport-color)] hover:bg-muted'
                            )}
                            onClick={() => handleOddsFormatChange(option.code)}
                            title={option.name}
                          >
                            <span className="font-medium">{option.name}</span>
                          </button>
                        ))}
                      </div>
                    </div>
                    {showLogout && (
                      <div className="space-y-2">
                        <label className="flex items-center text-sm font-medium text-foreground">
                          <i className="bi bi-box-arrow-right mr-2 text-[var(--sport-color)]" />
                          {t('LOGOUT')}
                        </label>
                        <button
                          type="button"
                          className="flex items-center px-3 py-2 border border-border rounded-md text-sm transition-all bg-background text-foreground hover:border-[var(--sport-color)] hover:bg-muted"
                          onClick={handleLogout}
                        >
                          <span className="font-medium">{t('LOGOUT')}</span>
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </Tab.Panel>
          </Tab.Panels>

          <div className="flex-shrink-0 bg-card/95 backdrop-blur supports-[backdrop-filter]:bg-card/80 border-t border-border z-[90] shadow-lg text-foreground">
            <Tab.List className="flex justify-around items-center h-16 px-2">
              <Tab
                className={({ selected }) =>
                  cn(
                    'flex flex-col items-center justify-center p-2 transition-colors min-w-[72px] rounded-lg',
                    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background',
                    selected ? 'text-[var(--sport-color)]' : 'text-muted-foreground hover:text-foreground'
                  )
                }
              >
                {({ selected }) => (
                  <>
                    <i className="bi bi-graph-up text-lg mb-0.5" />
                    <span className="text-xs font-semibold">{t('BETS')}</span>
                    {selected && <span className="mt-0.5 h-1 w-6 rounded-full bg-[var(--sport-color)]" aria-hidden="true" />}
                  </>
                )}
              </Tab>
              <Tab
                className={({ selected }) =>
                  cn(
                    'flex flex-col items-center justify-center p-2 transition-colors min-w-[72px] rounded-lg',
                    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background',
                    selected ? 'text-[var(--sport-color)]' : 'text-muted-foreground hover:text-foreground'
                  )
                }
              >
                {({ selected }) => (
                  <>
                    <i className={isLoggedIn ? 'bi bi-receipt text-lg mb-0.5' : 'bi bi-box-arrow-in-right text-lg mb-0.5'} />
                    <span className="text-xs font-semibold">{isLoggedIn ? t('MY_BETS', 'My Bets') : t('LOGIN', 'Login')}</span>
                    {selected && <span className="mt-0.5 h-1 w-6 rounded-full bg-[var(--sport-color)]" aria-hidden="true" />}
                  </>
                )}
              </Tab>
              <Tab
                className={({ selected }) =>
                  cn(
                    'flex flex-col items-center justify-center p-2 transition-colors min-w-[72px] rounded-lg',
                    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background',
                    selected ? 'text-[var(--sport-color)]' : 'text-muted-foreground hover:text-foreground'
                  )
                }
              >
                {({ selected }) => (
                  <>
                    <i className="bi bi-gear text-lg mb-0.5" />
                    <span className="text-xs font-semibold">{t('SETTINGS')}</span>
                    {selected && <span className="mt-0.5 h-1 w-6 rounded-full bg-[var(--sport-color)]" aria-hidden="true" />}
                  </>
                )}
              </Tab>
            </Tab.List>
          </div>
        </Tab.Group>
      )}
    </div>
  );
}

export default UnifiedMainContent;
